import axios, { InternalAxiosRequestConfig } from 'axios'
import { supabase } from '../supabase'

import { 
  VerifyPasswordRequest, 
  VerifyPasswordResponse, 
  ChangePasswordRequest, 
  ChangePasswordResponse 
} from './generated/models'

const api = axios.create({
  // In development, use relative URLs to leverage the Vite proxy
  baseURL: import.meta.env.DEV 
    ? '' // Empty string means use relative URLs
    : (import.meta.env.VITE_API_URL || 'http://localhost:8080'),
  headers: {
    'Content-Type': 'application/json',
  },
  // Always include credentials for cross-origin requests
  withCredentials: true,
  // Add timeout to prevent hanging requests
  timeout: 15000,
})

// Configure axios instance for all API calls
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token

  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 403 && error.response?.data?.code === 'USER_BANNED') {
      // Trigger a session refresh to update ban status
      const auth = (window as any).__SUPABASE_AUTH_PROVIDER__
      if (auth?.checkBanStatus) {
        await auth.checkBanStatus()
      }
    }
    return Promise.reject(error)
  }
)

/**
 * Verifies the current user's password
 * @param password The password to verify
 * @returns A promise that resolves to a VerifyPasswordResponse
 */
export const verifyPassword = async (password: string): Promise<VerifyPasswordResponse> => {
  const request: VerifyPasswordRequest = { password }
  const response = await api.post('/api/v1/users/verify-password', request)
  return response.data
}

/**
 * Changes the current user's password
 * @param currentPassword The current password
 * @param newPassword The new password
 * @returns A promise that resolves to a ChangePasswordResponse
 */
export const changePassword = async (currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> => {
  const request: ChangePasswordRequest = { currentPassword, newPassword }
  const response = await api.post('/api/v1/users/change-password', request)
  return response.data
}

export const getUserProfile = async () => {
  const response = await api.get('/api/v1/users/profile')
  return response.data
}

export { api }